import { getCountdownSeconds } from 'lib/helpers/getCountdownSeconds/getCountdownSeconds'
import { type VerifyView_PhoneFactorChallengeInfoFragment } from '../../components/VerifyView/VerifyView.graphql'

export interface IssuedChallenge {
  mfaToken: Maybe<string>
  phoneFactorChallengeInfo: Maybe<VerifyView_PhoneFactorChallengeInfoFragment>
}

let challengesByMethod: Record<string, IssuedChallenge> = {}

/**
 * Challenges that outlive the dialog, which `Modal.withHookedConditional` unmounts on close.
 *
 * A challenge reads back only while its send cooldown is still running — the same window in which the
 * backend refuses to send another code, and in which the code already on the user's phone is the
 * freshest one there is. A TOTP challenge carries no cooldown and so never reads back.
 *
 * That window also bounds how long a token lives here. It is the one-minute resend cooldown normally;
 * once the challenge's resends are spent the backend returns its expiry instead, which stretches reuse
 * to at most the challenge's own lifetime — still the point past which reuse would be wrong anyway.
 * {@link clearChallenges} drops the token sooner, the moment a verification spends it.
 */
export const readChallenge = (methodKey: string): IssuedChallenge | undefined => {
  const stored = challengesByMethod[methodKey]

  if (!stored) {
    return undefined
  }

  const cooldownSeconds = getCountdownSeconds({
    targetTimestamp: stored.phoneFactorChallengeInfo?.nextRetryAvailableAt,
    now: Date.now(),
  })

  return cooldownSeconds > 0 ? stored : undefined
}

export const writeChallenge = (methodKey: string, challenge: IssuedChallenge): void => {
  challengesByMethod[methodKey] = challenge
}

export const clearChallenges = (): void => {
  challengesByMethod = {}
}
